import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import BookingDayAgenda from './BookingDayAgenda';
import './BookingCalendarPanel.css';
import {
  formatLocalDate,
  getBookingEndDate,
  getBookingStartDate,
} from './bookingUtils';

export default function BookingCalendarPanel({
  calendarRef,
  calendarRenderKey,
  calendarDate,
  selectedDate,
  setCalendarDate,
  events,
  bookings,
  aircraftList,
  usersMap,
  trainingOptions,
  setSelectedDate,
  openBookingModal,
  setActiveBooking,
  setDetailsModalOpen,
  showOperationsBoard,
  openDayView,
  returnToCalendar,
  changeDayView,
}) {
  const todayLocalDate = formatLocalDate(new Date());
  const visibleBookings = getVisibleBookings(bookings, todayLocalDate);
  const visibleBookingIds = new Set(
    visibleBookings.map((booking) => String(booking.id))
  );
  const visibleEvents = (events || []).filter((event) =>
    visibleBookingIds.has(String(event.id))
  );
  const selectedDayBookings = getBookingsForDate(
    visibleBookings,
    selectedDate
  );

  function openBookingDetails(booking) {
    const aircraft = aircraftList.find((a) => Number(a.id) === Number(booking.aircraft_id));
    const pilot = usersMap[booking.user_id];
    const course = trainingOptions?.enrolments?.find(
      (enrolment) => String(enrolment.id) === String(booking.course_enrolment_id)
    )?.training_courses;

    setActiveBooking({
      ...booking,
      aircraftRegistration: aircraft?.registration || 'Aircraft',
      pilotName: pilot?.full_name || pilot?.email || 'Booked',
      bookingType: booking.booking_type || 'normal',
      studentName: usersMap[booking.student_user_id]?.full_name || usersMap[booking.student_user_id]?.email || '',
      instructorName: usersMap[booking.instructor_user_id]?.full_name || usersMap[booking.instructor_user_id]?.email || '',
      courseLabel: course ? [course.code, course.name].filter(Boolean).join(' · ') : '',
      pilotEmail: pilot?.email || '',
      pilotPhone: pilot?.phone || '',
    });

    setDetailsModalOpen(true);
  }

  return (
    <section className="booking-calendar-shell" style={styles.panel}>
      <div className={`booking-calendar-header${showOperationsBoard ? ' booking-calendar-header--operations' : ''}`} style={styles.header}>
        <div className="booking-calendar-heading">
          <h2 style={styles.title}>
            {showOperationsBoard ? 'Operations board' : 'Booking calendar'}
          </h2>
          <p style={styles.subtitle}>
            {showOperationsBoard
              ? 'Choose an aircraft and time slot for the selected day.'
              : 'Select a day to view aircraft availability.'}
          </p>
        </div>

        {showOperationsBoard ? (
          <button
            type="button"
            onClick={returnToCalendar}
            className="booking-calendar-back"
            style={styles.backButton}
          >
            ← Back to calendar
          </button>
        ) : (
          <div className="booking-calendar-month-control" style={styles.monthControl}>
            <button type="button" aria-label="Previous month" onClick={() => calendarRef.current?.getApi().prev()} style={styles.monthButton}>
              ‹
            </button>

            <div className="booking-calendar-month-title" aria-live="polite" style={styles.monthTitle}>
              {calendarDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </div>

            <button type="button" aria-label="Next month" onClick={() => calendarRef.current?.getApi().next()} style={styles.monthButton}>
              ›
            </button>
          </div>
        )}
      </div>

      {showOperationsBoard ? (
        <BookingDayAgenda
          selectedDate={selectedDate}
          selectedDayBookings={selectedDayBookings}
          aircraftList={aircraftList}
          usersMap={usersMap}
          trainingOptions={trainingOptions}
          openBookingModal={openBookingModal}
          openBookingDetails={openBookingDetails}
          getAircraftColour={getAircraftColour}
          changeDayView={changeDayView}
        />
      ) : (
        <FullCalendar
          key={calendarRenderKey}
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          height="auto"
          events={visibleEvents}
          displayEventTime={false}
          displayEventEnd={false}
          eventDisplay="block"
          eventOrder="start,title"
          eventContent={(argument) => (
            <span className="booking-calendar-event-content">
              <span>{argument.event.title}</span>
              {argument.event.extendedProps?.bookingType === 'training' && (
                <span className="booking-calendar-event-content__training">TRAINING</span>
              )}
            </span>
          )}
          firstDay={1}
          dayMaxEvents={3}
          datesSet={(info) => setCalendarDate(new Date(info.view.currentStart))}
          dayCellClassNames={(arg) => {
            const cellDate = formatLocalDate(arg.date);
            return cellDate === selectedDate ? ['wingit-selected-day'] : [];
          }}
          dateClick={(info) => {
            openDayView(info.dateStr);
          }}
          eventClick={(info) => {
            info.jsEvent.stopPropagation();

            const date = info.event.start;
            if (date) {
              openDayView(formatLocalDate(date));
            }

            const booking = visibleBookings.find((b) => String(b.id) === String(info.event.id));
            if (!booking) return;

            openBookingDetails(booking);
          }}
        />
      )}
    </section>
  );
}

function getVisibleBookings(bookings, todayLocalDate) {
  return (bookings || []).filter((booking) =>
    isBookingVisibleOnOrAfterDate(booking, todayLocalDate)
  );
}

export function isBookingVisibleOnOrAfterDate(booking, todayLocalDate) {
  const start = getBookingStartDate(booking);
  const end = getBookingEndDate(booking);

  if (!start || !end) return false;

  return formatLocalDate(end) >= todayLocalDate;
}

function getBookingsForDate(bookings, selectedDate) {
  if (!selectedDate) return [];

  return (bookings || [])
    .filter((booking) => {
      const start = getBookingStartDate(booking);
      const end = getBookingEndDate(booking);
      if (!start || !end) return booking.date === selectedDate;

      const dayStart = new Date(`${selectedDate}T00:00:00`);
      const dayEnd = new Date(`${selectedDate}T23:59:59`);

      return start <= dayEnd && end >= dayStart;
    })
    .sort((left, right) => {
      const leftStart = getBookingStartDate(left);
      const rightStart = getBookingStartDate(right);
      return (leftStart?.getTime() || 0) - (rightStart?.getTime() || 0);
    });
}

const aircraftColours = [
  '#2563eb',
  '#facc15',
  '#ec4899',
  '#16a34a',
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#14b8a6',
  '#eab308',
  '#3b82f6',
];

function getFallbackAircraftColour(identifier) {
  const text = String(identifier || '').trim().toUpperCase();
  if (!text) return aircraftColours[0];

  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }

  return aircraftColours[hash % aircraftColours.length];
}

function getAircraftColour(aircraft, aircraftList) {
  if (!aircraft) return getFallbackAircraftColour('');

  const numericId = Number(aircraft.id);
  if (Number.isFinite(numericId) && numericId > 0) {
    return aircraftColours[(numericId - 1) % aircraftColours.length];
  }

  const orderedAircraft = [...(aircraftList || [])].sort(
    (left, right) => Number(left?.id || 0) - Number(right?.id || 0)
  );

  const aircraftIndex = orderedAircraft.findIndex(
    (entry) => Number(entry?.id) === Number(aircraft.id)
  );

  if (aircraftIndex >= 0) {
    return aircraftColours[aircraftIndex % aircraftColours.length];
  }

  return getFallbackAircraftColour(aircraft.registration || aircraft.id);
}

const styles = {
  panel: {
    background: 'linear-gradient(180deg, rgba(35,36,58,0.92) 0%, rgba(25,32,52,0.96) 100%)',
    borderRadius: 20,
    padding: '1.1rem',
    border: '1px solid rgba(85,199,255,0.18)',
    color: '#d7ebff',
    marginBottom: '1.2rem',
    boxShadow: '0 18px 46px rgba(0,0,0,0.26)',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '0.9rem',
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '1.35rem',
    fontWeight: 750,
    letterSpacing: '-0.03em',
  },

  subtitle: {
    margin: '0.3rem 0 0',
    color: '#9fb6d1',
    fontSize: '0.9rem',
    lineHeight: 1.35,
  },

  monthControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    background: 'rgba(10,22,36,0.55)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 999,
    padding: '0.3rem',
  },

  monthButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    border: '1px solid rgba(85,199,255,0.25)',
    background: 'rgba(85,199,255,0.08)',
    color: '#d7ebff',
    fontSize: '1.35rem',
    lineHeight: 1,
    cursor: 'pointer',
  },

  monthTitle: {
    minWidth: 135,
    textAlign: 'center',
    color: '#d7ebff',
    fontSize: '0.92rem',
    fontWeight: 700,
  },

  backButton: {
    border: '1px solid rgba(85,199,255,0.28)',
    background: 'rgba(85,199,255,0.10)',
    color: '#d7ebff',
    borderRadius: 999,
    padding: '0.55rem 0.9rem',
    fontSize: '0.84rem',
    fontWeight: 800,
    cursor: 'pointer',
  },
};